import type { Limrun } from "@limrun/api/client";
import { transformUrl } from "@/auth/auth";
import { signalingRequest } from "@/lib/signaling";

export function parseLaunchEnvironment(params: URLSearchParams): Record<string, string> {
  const entries = params.getAll("env");
  if (entries.length && params.get("platform") !== "ios") {
    throw new Error("Preview environment variables are supported only for iOS.");
  }
  return Object.fromEntries(entries.map((entry) => {
    const separator = entry.indexOf("=");
    if (separator < 1) throw new Error("Preview env must use KEY=VALUE.");
    return [entry.slice(0, separator), entry.slice(separator + 1)];
  }));
}

// The key under which the simulator restores and saves the organization's
// device state. Only limulator implements it, so an Android link carrying one
// fails before a device is provisioned, like preview environment variables.
// `{user}` in a key gives each reviewer their own state under one shared link: the console
// expands it with the signed-in email address, which the key charset allows. A key without the
// placeholder is shared by the whole organization, like an Xcode snapshot key.
export function expandDeviceStateKey(key: string, emailAddress: string): string {
  return key.split("{user}").join(emailAddress);
}

export function parseDeviceStateKey(params: URLSearchParams): string | undefined {
  const keys = ["snapshot-key", "simulatorSnapshot", "deviceState"]
    .map(name => params.get(name))
    .filter((key): key is string => Boolean(key));
  if (new Set(keys).size > 1) {
    throw new Error("snapshot-key, simulatorSnapshot, and deviceState must name the same snapshot.");
  }
  const key = keys[0];
  if (key && params.get("platform") !== "ios") {
    throw new Error("Device state is supported only for iOS previews.");
  }
  return key;
}

type DeviceConnection = { status: Pick<Limrun.IosInstance["status"], "token" | "endpointWebSocketUrl"> };

function deviceSignalingUrl(instance: DeviceConnection): string {
  const endpoint = transformUrl(instance.status.endpointWebSocketUrl);
  if (!endpoint || !instance.status.token) {
    throw new Error("Preview is missing the instance connection.");
  }
  const url = new URL(endpoint);
  url.searchParams.set("token", instance.status.token);
  return url.toString();
}

export async function openDeviceUrl(instance: DeviceConnection, url: string): Promise<void> {
  await signalingRequest(deviceSignalingUrl(instance), "openUrl", { url }, 30_000);
}

export type DeviceStateSaveResult = { bytes?: number; apps?: string[] };

// The simulator requests an upload URL at save time and stops apps while copying their data.
export async function saveDeviceState(instance: DeviceConnection): Promise<DeviceStateSaveResult> {
  return signalingRequest(deviceSignalingUrl(instance), "exportDeviceState", {}, 600_000);
}

export async function installDeviceApp(instance: DeviceConnection, asset: Limrun.Asset): Promise<string> {
  if (!asset.signedDownloadUrl) throw new Error("Preview launch is missing the asset download URL.");
  const installed = await signalingRequest(deviceSignalingUrl(instance), "appInstallation", {
    url: asset.signedDownloadUrl,
    md5: asset.md5,
  }, 120_000);
  if (typeof installed.bundleId !== "string" || !installed.bundleId) {
    throw new Error("Preview installation did not return an app bundle identifier.");
  }
  return installed.bundleId;
}

export async function launchDeviceApp(instance: DeviceConnection, bundleId: string, env: Record<string, string>): Promise<void> {
  await signalingRequest(deviceSignalingUrl(instance), "launchApp", {
    bundleId, mode: "RelaunchIfRunning", env,
  }, 30_000);
}

export class LegacySnapshotAppsRequiredError extends Error {
  constructor(bundleIds: string[]) {
    super(`This older snapshot has no app bundles. Install these apps and restore again: ${bundleIds.join(", ")}.`);
  }
}

export async function restoreDeviceSnapshot(instance: DeviceConnection, downloadUrl: string): Promise<string> {
  if (!downloadUrl) return "Starting fresh";
  const result = await signalingRequest(deviceSignalingUrl(instance), "importDeviceState", { url: downloadUrl }, 900_000);
  if (result.keychainError) throw new Error(`Snapshot keychain could not be restored: ${result.keychainError}`);
  if (Array.isArray(result.appsPending) && result.appsPending.length) {
    throw new LegacySnapshotAppsRequiredError(result.appsPending);
  }
  return "Restored snapshot";
}

// Install without auto-launch so even the app's first process receives the environment.
export async function installAndLaunchApp(
  instance: DeviceConnection,
  asset: Limrun.Asset,
  platform: "ios" | "android",
  env: Record<string, string>,
  onLaunching: () => void,
  isActive: () => boolean = () => true,
): Promise<void> {
  if (!asset.signedDownloadUrl) {
    throw new Error("Preview launch is missing the asset download URL.");
  }
  const url = deviceSignalingUrl(instance);
  if (platform === "android") {
    await signalingRequest(url, "appInstallation", {
      name: asset.name,
      urls: [asset.signedDownloadUrl],
      ...(asset.md5 ? { md5List: [asset.md5] } : {}),
      launchMode: "ForegroundIfRunning",
    }, 120_000, { type: "appInstallation", key: "name", value: asset.name });
    return;
  }
  const bundleId = await installDeviceApp(instance, asset);
  if (!isActive()) return;
  onLaunching();
  await launchDeviceApp(instance, bundleId, env);
}

// The published SDK does not yet expose the snapshot restoration response.
export type DeviceStateStatus = {
  restore: { phase: "pending" | "restored" | "empty" | "failed"; downloadUrl?: string; reason?: string; message?: string };
  save: { phase: string; reason?: string; message?: string; sizeBytes?: number };
};
